package fr.manyverse.app.domain.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Sealed class representing the content of different event types.
 */
@Serializable
sealed class EventContent {
    /**
     * Content for announcement events.
     * At least one of text or imageUrl must be provided.
     */
    @Serializable
    @SerialName("announcement")
    data class Announcement(
        @SerialName("text")
        val text: String? = null,

        @SerialName("image_url")
        val imageUrl: String? = null
    ) : EventContent()

    /**
     * Content for whiteboard opened events.
     * Records when a whiteboard was opened for historical purposes.
     */
    @Serializable
    @SerialName("whiteboard_opened")
    data class WhiteboardOpened(
        @SerialName("whiteboard_id")
        val whiteboardId: String
    ) : EventContent()

    /**
     * Content for timer started events.
     * Records when a timer was started for historical purposes.
     */
    @Serializable
    @SerialName("timer_started")
    data class TimerStarted(
        @SerialName("timer_id")
        val timerId: String,

        @SerialName("duration_seconds")
        val durationSeconds: Int
    ) : EventContent()

    @Serializable
    @SerialName("digicode_launched")
    data class DigicodeLaunched(
        @SerialName("digicode_id")
        val digicodeId: String,

        @SerialName("digit_count")
        val digitCount: Int
    ) : EventContent()

    @Serializable
    @SerialName("lockpicking_launched")
    data class LockpickingLaunched(
        @SerialName("lockpicking_id")
        val lockpickingId: String,

        @SerialName("lock_count")
        val lockCount: Int
    ) : EventContent()

    @Serializable
    @SerialName("dice_rolled")
    data class DiceRolled(
        @SerialName("character_name")
        val characterName: String,

        @SerialName("die_type")
        val dieType: String,

        @SerialName("count")
        val count: Int,

        @SerialName("results")
        val results: List<Int>,

        @SerialName("is_gm_roll")
        val isGmRoll: Boolean = false,

        @SerialName("modifier")
        val modifier: Int = 0
    ) : EventContent()

    @Serializable
    @SerialName("message_sent")
    data class MessageSent(
        @SerialName("text") val text: String,
        @SerialName("sender_name") val senderName: String
    ) : EventContent()

    @Serializable
    @SerialName("player_joined")
    data class PlayerJoined(
        @SerialName("character_name")
        val characterName: String
    ) : EventContent()

    @Serializable
    @SerialName("player_kicked")
    class PlayerKicked : EventContent()

    @Serializable
    @SerialName("player_alert")
    data class PlayerAlert(
        @SerialName("character_name") val characterName: String,
        @SerialName("alert_type") val alertType: AlertType,
        @SerialName("message") val message: String? = null
    ) : EventContent()

    @Serializable
    @SerialName("announcement_cancelled")
    data class AnnouncementCancelled(
        @SerialName("announcement_event_id")
        val announcementEventId: String? = null
    ) : EventContent()

    /**
     * Content for combat started events.
     * Records when the GM launched a combat for historical purposes.
     */
    @Serializable
    @SerialName("combat_started")
    data class CombatStarted(
        @SerialName("combat_id")
        val combatId: String,

        @SerialName("participant_count")
        val participantCount: Int
    ) : EventContent()

    /**
     * Content for item sent events: an inventory item given to one or more players.
     * Only the GM displays the item name; recipients are just told who sent them something.
     */
    @Serializable
    @SerialName("item_sent")
    data class ItemSent(
        /** Name of the sending character, or null when the GM sent the item. */
        @SerialName("sender_name")
        val senderName: String? = null,

        @SerialName("recipient_names")
        val recipientNames: List<String>,

        @SerialName("item_name")
        val itemName: String,

        @SerialName("quantity")
        val quantity: Int
    ) : EventContent()
}
